import type { StandInArt, StandInDefinition } from "daedalus-core";
import { BODY_HEIGHT, BODY_WIDTH, OCCLUDERS } from "../client/body.ts";
import { FOOT_HALF_WIDTH, FOOT_HEIGHT, SIXTEENTHS } from "../movement/step.ts";
import { BAKED_SHEET } from "../sprite/look-bake.ts";
import type { SheetDescription } from "../sprite/sheet.ts";
import { WORLD_TILE_PIXELS } from "../vision/vision.ts";

/**
 * OdysseyTales's stand-in for the editor's player layer (ADR-071 decisions 1 and 10): the body box and the
 * rule the occluder fade tests and the feet box the inside test reads, in world pixels, with a player standing as
 * its art once the page has read the character manifest, whose file names are content hashes. The player is the
 * fallback look every character wears until its own is ready, which the build bakes to a one-layer character sheet
 * (ADR-094 decision 33, `sprite/look-bake.ts`).
 */

/**
 * The character manifest entry the stand-in's art comes from: the baked fallback look.
 */
export const STAND_IN_CHARACTER = BAKED_SHEET;

/**
 * The stand-in's sizes, without art: the body box, the feet box converted from sixteenths of a tile with the
 * pixels a sixteenth is, the step between two positions a character can stand at, and the occluder rule the
 * game fades props over its local character by.
 */
export const ODYSSEY_STAND_IN: StandInDefinition = {
  width: BODY_WIDTH,
  height: BODY_HEIGHT,
  feetBox: { halfWidth: (FOOT_HALF_WIDTH * WORLD_TILE_PIXELS) / SIXTEENTHS, height: (FOOT_HEIGHT * WORLD_TILE_PIXELS) / SIXTEENTHS, step: WORLD_TILE_PIXELS / SIXTEENTHS },
  occluder: OCCLUDERS,
};

/**
 * The stand-in's art: a character standing, the `idle` strip facing `down`, frame 0, on the sheet's frame
 * size and pivot, as `CharacterSprite` draws it.
 *
 * @param sheet The character's validated sheet.
 * @param base URL prefix the manifest's `assets/...` paths are relative to.
 * @returns The art.
 * @throws Error when the sheet has no idle animation.
 */
export function odysseyStandInArt(sheet: SheetDescription, base: string): StandInArt {
  const idle = sheet.anims["idle"];
  if (idle === undefined) {
    throw new Error("the stand-in's sheet has no idle animation");
  }
  return { url: `${base}/${idle.files.down}`, frameWidth: sheet.frameWidth, frameHeight: sheet.frameHeight, frame: 0, pivot: { x: sheet.pivot.x, y: sheet.pivot.y } };
}
